import { Box, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';
import { ExternalLinkIcon } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { Resource } from '../../../types/Resources/Resource.type.ts';
import { ResourceType } from '../../../types/enums/ResourceType.enum.ts';
import ExternalLinkWarning from '../../ExternalLinkWarning/ExternalLinkWarning.tsx';

export interface ResourceSelectionItemProps {
  caseResource: Resource;
  selectedResourceId: number | null;
  onResourceSelect: (resourceId: number) => void;
  updateResourceMutation: (data: { caseId: number; resourceId: number; pinned: boolean }) => void;
  isUpdatingResource?: boolean;
}

function ResourceSelectionItem({
  caseResource,
  selectedResourceId,
  onResourceSelect,
  updateResourceMutation,
  isUpdatingResource,
}: ResourceSelectionItemProps) {
  const [linkWarningOpen, setLinkWarningOpen] = useState(false);
  const [pendingLink, setPendingLink] = useState('');

  const handleCheckboxClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
  };

  const { caseId } = useParams<{ caseId?: string }>();
  const isLink = caseResource.type.id === ResourceType.LINK;

  const handleBoxClick = () => {
    if (isLink && caseResource.link) {
      setPendingLink(caseResource.link);
      setLinkWarningOpen(true);
    } else {
      onResourceSelect(caseResource.id);
    }
  };

  const handleConfirmLink = () => {
    window.open(pendingLink, '_blank');
    setLinkWarningOpen(false);
    setPendingLink('');
  };

  const handleCancelLink = () => {
    setLinkWarningOpen(false);
    setPendingLink('');
  };

  return (
    <>
      <Box
        key={caseResource.id}
        onClick={handleBoxClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #e2e8f0',
          borderColor: selectedResourceId === caseResource.id ? 'accentBlue.main' : '#e2e8f0',
          borderWidth: selectedResourceId === caseResource.id ? '2px' : '1px',
          cursor: 'pointer',
          borderRadius: '6px',
          paddingX: '20px',
          paddingY: '28px',
        }}
      >
        <FormControlLabel
          control={
            isUpdatingResource ? (
              <CircularProgress size={26} sx={{ marginX: '10px' }} />
            ) : (
              <Checkbox
                onChange={() =>
                  updateResourceMutation({
                    caseId: Number(caseId),
                    resourceId: caseResource.id,
                    pinned: !caseResource.pinned,
                  })
                }
                onClick={handleCheckboxClick}
                checked={caseResource.pinned}
                sx={{ margin: 0, '&.Mui-checked': { color: 'accentBlue.main' } }}
              />
            )
          }
          label={
            <Box onClick={(e) => e.preventDefault()}>
              <Box sx={{ display: 'flex', alignItems: 'top', gap: '6px' }}>
                <Typography
                  variant='body1'
                  sx={{
                    fontWeight: 600,
                    background: 'linear-gradient(270deg, #3898F2 0%, #2A09FD 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {caseResource.name}
                </Typography>
                {isLink && <ExternalLinkIcon size={18} strokeWidth={2} color='#3465F6' />}
              </Box>
              <Typography variant='body2' sx={{ fontSize: '12px', color: '#718096' }}>
                {caseResource.description}
              </Typography>
            </Box>
          }
        />
      </Box>
      <ExternalLinkWarning
        open={linkWarningOpen}
        url={pendingLink}
        onConfirm={handleConfirmLink}
        onCancel={handleCancelLink}
      />
    </>
  );
}

export default ResourceSelectionItem;
